/**
 * Public blog post detail page — published article from AI Blog admin pipeline.
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Gift, Loader2, Tag } from 'lucide-react';
import { getPublicBlogPost } from '../services/apiClient';
import { setSEO, injectJsonLd } from '../utils/seo';
import { DEFAULT_BLOG_IMAGE, getBlogCoverImage } from '../utils/blogImages';
import BlogCover from '../components/BlogCover';

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await getPublicBlogPost(slug);
        if (!cancelled) setPost(res?.post || null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Post not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const cover = getBlogCoverImage(post);
    setSEO({
      title: `${post.title} — Tolbert Tactical`,
      description: post.metaDescription || post.excerpt || '',
      path: `/blog/${post.slug}`,
      image: cover,
    });
    injectJsonLd('blog-post-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      datePublished: post.publishedAt || post.createdAt,
      author: { '@type': 'Organization', name: 'Tolbert Tactical' },
      description: post.excerpt || '',
      image: cover,
    });
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-black flex flex-col items-center justify-center gap-3 py-24">
        <Loader2 className="h-8 w-8 animate-spin text-theme-bronze" />
        <p className="text-sm text-theme-silver/60">Loading article…</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-theme-black">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="glass-card-dark p-10">
            <p className="text-theme-silver text-lg mb-2">{error || 'Post not found'}</p>
            <p className="text-theme-silver/60 text-sm mb-6">
              This article may have been unpublished or the link is incorrect.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-theme-bronze hover:text-theme-bronze-light transition-colors font-medium"
            >
              <ArrowLeft className="h-4 w-4" /> Back to blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-black">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm text-theme-bronze hover:text-theme-bronze-light transition-colors mb-8 group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to blog
        </Link>

        <BlogCover
          post={post}
          alt=""
          className="mb-8 rounded-2xl border border-white/10 aspect-[21/9] sm:aspect-[2.4/1] min-h-[10rem]"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-theme-black/50 via-transparent to-transparent pointer-events-none" />
        </BlogCover>

        <header className="mb-10">
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-theme-bronze/15 text-theme-bronze"
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                </span>
              ))}
            </div>
          )}

          <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-theme-silver leading-tight mb-5">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-theme-silver/55">
            {post.publishedAt && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
              </span>
            )}
            <span className="text-theme-silver/30">·</span>
            <span>Tolbert Tactical</span>
          </div>

          {post.excerpt && (
            <p className="mt-6 text-lg text-theme-silver-light leading-relaxed border-l-2 border-theme-bronze/40 pl-4">
              {post.excerpt}
            </p>
          )}

          <div className="mt-8 h-px bg-gradient-to-r from-theme-bronze/40 via-theme-silver/10 to-transparent" />
        </header>

        <div
          className="blog-content text-theme-silver"
          dangerouslySetInnerHTML={{ __html: post.bodyHtml || '' }}
        />

        <footer className="mt-14 pt-8 border-t border-theme-silver/10">
          <div className="glass-card-dark p-6 md:p-8 flex flex-col sm:flex-row sm:items-center gap-5 border border-theme-bronze/15">
            <div className="glass-card-dark p-3 rounded-xl shrink-0 self-start">
              <Gift className="h-6 w-6 text-theme-bronze" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-theme-silver mb-1">
                Enter this week&apos;s giveaways
              </h2>
              <p className="text-sm text-theme-silver/65">
                Premium tactical gear — free entries available with no purchase necessary.
              </p>
            </div>
            <Link
              to="/giveaways"
              className="btn-primary inline-flex items-center justify-center gap-2 shrink-0 whitespace-nowrap"
            >
              View giveaways <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-theme-silver/60 hover:text-theme-bronze transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> More articles
            </Link>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default BlogPost;
